// ─────────────────────────────────────────────────────────────────
// Email calendar invite
// ----------------------------------------------------------------

import {
  EmailCalendarSchema,
  type EmailCalendar,
  type EmailCalendarInput,
} from '@blogforge/shared';
import { CalendarPlus, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { ApiError } from '../../hooks/api';
import { Validators } from '../../lib/validators';

/** A name the RFC 5545 TZID / Intl both understand, e.g. `Africa/Lagos` or `UTC`. */
const TIME_ZONE = /^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+)*$/;

/** `YYYY-MM-DD` (all-day) or `YYYY-MM-DDTHH:mm` (timed) — what the inputs hold. */
const WALL_CLOCK = /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2})?$/;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const pad = (value: number) => String(value).padStart(2, '0');

/** A moment as the wall clock the browser shows for it — what `datetime-local` wants. */
function wallClockOf(date: Date): string {
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/** A stored instant as a wall clock, or `''` when it is not a date we can read. */
function wallClockFromInstant(iso: string): string {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? wallClockOf(new Date(ms)) : '';
}

/**
 * A stored instant as the instant it names. `datetime-local` values have no
 * offset, and `new Date` reads those as local time, which is the convention
 * this panel states in its help text.
 */
function instantFromWallClock(value: string): Date | null {
  if (!WALL_CLOCK.test(value)) return null;
  // A bare `YYYY-MM-DD` parses as UTC midnight, which is a different day east
  // and west of Greenwich; all-day values never travel through here.
  if (DATE_ONLY.test(value)) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? new Date(ms) : null;
}

/** Reads a non-empty string field, keeping anything that is not one out of the draft. */
function readString(source: Record<string, unknown>, key: string): string | undefined {
  const value = source[key];
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

/**
 * Reads the invite off the email row. Deliberately tolerant rather than a
 * `safeParse`: a record the schema would reject still has to reach the panel,
 * where it can be corrected — hiding it behind "no invite yet" would invite an
 * overwrite. `null` means there genuinely is no event (no start/end pair).
 */
export function parseCalendarInvite(raw: unknown): EmailCalendar | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const source = raw as Record<string, unknown>;

  const start = readString(source, 'start');
  const end = readString(source, 'end');
  if (!start || !end) return null;

  const summary = readString(source, 'summary');
  const description = readString(source, 'description');
  const location = readString(source, 'location');
  const url = readString(source, 'url');
  const timezone = readString(source, 'timezone');
  const id = readString(source, 'id');

  return {
    name: readString(source, 'name') ?? '',
    type: source['type'] === 'publish' ? 'publish' : 'request',
    start,
    end,
    allDay: source['allDay'] === true,
    ...(summary ? { summary } : {}),
    ...(description ? { description } : {}),
    ...(location ? { location } : {}),
    ...(url ? { url } : {}),
    ...(timezone ? { timezone } : {}),
    ...(id ? { id } : {}),
  };
}

/**
 * Content snapshot, independent of key order — Postgres hands jsonb back with
 * its own ordering, so comparing the raw JSON would report a change after every
 * refetch and throw away the fields being edited.
 */
export function inviteSnapshot(invite: EmailCalendar | null): string {
  if (!invite) return 'none';
  return JSON.stringify([
    invite.name,
    invite.type,
    invite.start,
    invite.end,
    invite.allDay ?? false,
    invite.summary ?? null,
    invite.description ?? null,
    invite.location ?? null,
    invite.url ?? null,
    invite.timezone ?? null,
    invite.id ?? null,
  ]);
}

/** What the form holds: every field a string (or a toggle), so an empty one can be typed into. */
export interface InviteDraft {
  name: string;
  type: EmailCalendar['type'];
  allDay: boolean;
  start: string;
  end: string;
  summary: string;
  description: string;
  location: string;
  url: string;
  timezone: string;
  uid: string;
}

function draftOf(invite: EmailCalendar | null): InviteDraft {
  if (!invite) return emptyDraft();
  return {
    name: invite.name,
    type: invite.type,
    allDay: invite.allDay ?? false,
    // An all-day value is a plain date; a timed one is an instant that the
    // input has to show as a wall clock.
    start: invite.allDay ? invite.start.slice(0, 10) : wallClockFromInstant(invite.start),
    end: invite.allDay ? invite.end.slice(0, 10) : wallClockFromInstant(invite.end),
    summary: invite.summary ?? '',
    description: invite.description ?? '',
    location: invite.location ?? '',
    url: invite.url ?? '',
    timezone: invite.timezone ?? '',
    uid: invite.id ?? '',
  };
}

/** A new invite: the next whole hour, an hour long — a starting point, not empty fields. */
function emptyDraft(): InviteDraft {
  const start = new Date();
  start.setMinutes(0, 0, 0);
  start.setHours(start.getHours() + 1);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return {
    name: '',
    type: 'request',
    allDay: false,
    start: wallClockOf(start),
    end: wallClockOf(end),
    summary: '',
    description: '',
    location: '',
    url: '',
    timezone: '',
    uid: '',
  };
}

/**
 * The draft as the record the route stores, or `null` while it cannot be one
 * (a half-typed date). Optional fields are dropped when empty: an empty `url`
 * would fail the schema's URL check, and the rest would be noise on the row.
 */
export function inviteInputOf(draft: InviteDraft): EmailCalendarInput | null {
  const start = instantFromWallClock(draft.start);
  const end = instantFromWallClock(draft.end);

  let startValue: string;
  let endValue: string;
  if (draft.allDay) {
    if (!DATE_ONLY.test(draft.start) || !DATE_ONLY.test(draft.end)) return null;
    startValue = draft.start;
    endValue = draft.end;
  } else {
    if (!start || !end) return null;
    startValue = start.toISOString();
    endValue = end.toISOString();
  }

  const summary = draft.summary.trim();
  const description = draft.description.trim();
  const location = draft.location.trim();
  const url = draft.url.trim();
  const timezone = draft.timezone.trim();
  const uid = draft.uid.trim();

  return {
    name: draft.name.trim(),
    type: draft.type,
    start: startValue,
    end: endValue,
    allDay: draft.allDay,
    ...(summary === '' ? {} : { summary }),
    ...(description === '' ? {} : { description }),
    ...(location === '' ? {} : { location }),
    ...(url === '' ? {} : { url }),
    ...(timezone === '' ? {} : { timezone }),
    ...(uid === '' ? {} : { id: uid }),
  };
}

/** Client-side mirror of EmailCalendarSchema. */
export function inviteProblem(draft: InviteDraft): string | null {
  if (draft.name.trim().length < 3) {
    return 'Give the calendar a name of at least 3 characters — recipients see it as the calendar the event belongs to.';
  }

  const timed = !draft.allDay;
  const start = timed ? instantFromWallClock(draft.start) : draft.start;
  const end = timed ? instantFromWallClock(draft.end) : draft.end;
  if (!start) return 'Give the invite a start date and time.';
  if (!end) return 'Give the invite an end date and time.';

  // DTEND has to land after DTSTART, or a client shows a zero-length event.
  const ordered = timed
    ? (start as Date).getTime() < (end as Date).getTime()
    : (start as string) < (end as string);
  if (!ordered) return 'The invite has to end after it starts.';

  const url = draft.url.trim();
  if (url !== '' && !Validators.isValidUrl(url)) {
    return 'The event link has to be a full URL, such as https://example.com/launch.';
  }

  const timezone = draft.timezone.trim();
  if (timezone !== '' && !TIME_ZONE.test(timezone)) {
    return 'The timezone looks off — use an IANA name such as Africa/Lagos, or leave it blank.';
  }

  const input = inviteInputOf(draft);
  if (!input) return 'This invite is missing something a calendar needs.';
  const parsed = EmailCalendarSchema.safeParse(input);
  if (!parsed.success) {
    const [first] = parsed.error.issues;
    return first ? `${first.path.join('.') || 'invite'}: ${first.message}` : 'Invalid invite.';
  }
  return null;
}

/** The route answers with a zod issue array — surface the first issue. */
function inviteErrorMessage(cause: unknown): string {
  if (cause instanceof ApiError) {
    const detail: unknown = cause.body?.['error'];
    if (Array.isArray(detail) && detail.length > 0) {
      const [first] = detail as unknown[];
      if (first && typeof first === 'object') {
        const issue = first as { message?: unknown; path?: unknown };
        const path =
          Array.isArray(issue.path) && issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
        return `${path}${typeof issue.message === 'string' ? issue.message : 'Invalid invite'}`;
      }
    }
    return cause.message;
  }
  return cause instanceof Error ? cause.message : 'Could not save the invite.';
}

interface EmailCalendarInviteEditorProps {
  /** The invite currently stored on the email (server truth), if any. */
  rawInvite: unknown;
  /** Persists the whole record — the endpoint replaces it. */
  onSave: (next: EmailCalendarInput) => Promise<unknown>;
  /** Clears it. The email is sent without an event afterwards. */
  onDelete: () => Promise<unknown>;
}

/**
 * The single calendar event this email carries. Unlike the variables and
 * attachments editors this is one record with fields that only make sense
 * together, so the whole thing is written on an explicit save rather than on
 * every blur — a half-filled invite is a 422, not a saved state.
 */
export function EmailCalendarInviteEditor({
  rawInvite,
  onSave,
  onDelete,
}: EmailCalendarInviteEditorProps) {
  const invite = parseCalendarInvite(rawInvite);

  const [working, setWorking] = useState<InviteDraft | null>(() =>
    invite ? draftOf(invite) : null,
  );
  const [syncedFrom, setSyncedFrom] = useState(() => inviteSnapshot(invite));
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'removed' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  // Adopt the stored value whenever the query hands back a different one, but
  // leave an echo of our own save (and anything still being edited) alone.
  const stored = inviteSnapshot(invite);
  if (syncedFrom !== stored) {
    setSyncedFrom(stored);
    if (stored !== inviteSnapshot(working ? inviteInputOf(working) : null)) {
      setWorking(invite ? draftOf(invite) : null);
      setStatus('idle');
      setError(null);
    }
  }

  const busy = status === 'saving';
  const open = working !== null;

  const patch = (changes: Partial<InviteDraft>) =>
    setWorking((prev) => (prev ? { ...prev, ...changes } : prev));

  const apply = (next: InviteDraft) => {
    const problem = inviteProblem(next);
    const input = problem ? null : inviteInputOf(next);
    if (problem || !input) {
      // Keep the edit on screen but out of the API — the record must stay valid.
      setStatus('error');
      setError(problem ?? 'This invite is missing something a calendar needs.');
      return;
    }
    setStatus('saving');
    setError(null);
    void onSave(input).then(
      () => setStatus('saved'),
      (cause: unknown) => {
        setStatus('error');
        setError(inviteErrorMessage(cause));
      },
    );
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (working) apply(working);
  };

  /** Leaves a fresh invite without writing anything. */
  const cancel = () => {
    setWorking(null);
    setStatus('idle');
    setError(null);
  };

  const remove = () => {
    if (
      !confirm(
        'Remove the calendar invite? This email will be sent without an event, and recipients who already have it keep theirs.',
      )
    ) {
      return;
    }
    setStatus('saving');
    setError(null);
    void onDelete().then(
      () => {
        setWorking(null);
        setStatus('removed');
      },
      (cause: unknown) => {
        setStatus('error');
        setError(inviteErrorMessage(cause));
      },
    );
  };

  return (
    <div className="mt-2 space-y-2">
      {!open && (
        <div className="space-y-2">
          <p className="text-muted text-xs">
            No calendar invite yet — add one and recipients can accept the event straight from the
            email.
          </p>
          <button
            type="button"
            className="btn-secondary w-full py-1.5 text-xs"
            onClick={() => {
              setWorking(emptyDraft());
              setStatus('idle');
              setError(null);
            }}
          >
            <CalendarPlus size={13} strokeWidth={2.6} />
            Add calendar invite
          </button>
        </div>
      )}

      {open && working && (
        <form
          onSubmit={submit}
          className="border-line bg-card space-y-2 rounded-lg border p-2.5"
          aria-label={invite ? 'Calendar invite' : 'New calendar invite'}
        >
          <input
            className="field-input px-2 py-1 text-base md:text-xs"
            placeholder="Calendar name, e.g. Acme Events"
            aria-label="Calendar name"
            value={working.name}
            disabled={busy}
            onChange={(e) => patch({ name: e.target.value })}
          />

          <select
            title="Invite type"
            aria-label="Invite type"
            className="field-input px-2 py-1 text-xs"
            value={working.type}
            disabled={busy}
            onChange={(e) => patch({ type: e.target.value as InviteDraft['type'] })}
          >
            <option value="request">Request — ask the recipient to accept</option>
            <option value="publish">Publish — add it to their calendar</option>
          </select>

          <label className="text-muted flex cursor-pointer items-center gap-1.5 text-[11px]">
            <input
              type="checkbox"
              checked={working.allDay}
              disabled={busy}
              onChange={(e) => patch({ allDay: e.target.checked })}
            />
            All day
          </label>

          <div>
            <label className="field-label" htmlFor="calendar-invite-start">
              Starts
            </label>
            <input
              id="calendar-invite-start"
              type={working.allDay ? 'date' : 'datetime-local'}
              className="field-input px-2 py-1 text-base md:text-xs"
              value={working.allDay ? working.start.slice(0, 10) : working.start}
              disabled={busy}
              onChange={(e) => patch({ start: e.target.value })}
            />
          </div>

          <div>
            <label className="field-label" htmlFor="calendar-invite-end">
              Ends
            </label>
            <input
              id="calendar-invite-end"
              type={working.allDay ? 'date' : 'datetime-local'}
              className="field-input px-2 py-1 text-base md:text-xs"
              value={working.allDay ? working.end.slice(0, 10) : working.end}
              disabled={busy}
              onChange={(e) => patch({ end: e.target.value })}
            />
          </div>

          <input
            className="field-input px-2 py-1 text-base md:text-xs"
            placeholder="Title, e.g. Product launch"
            aria-label="Event title"
            value={working.summary}
            disabled={busy}
            onChange={(e) => patch({ summary: e.target.value })}
          />

          <input
            className="field-input px-2 py-1 text-base md:text-xs"
            placeholder="Location, e.g. 12 Marina Road, Lagos"
            aria-label="Event location"
            value={working.location}
            disabled={busy}
            onChange={(e) => patch({ location: e.target.value })}
          />

          <input
            className="field-input px-2 py-1 text-base md:text-xs"
            placeholder="Link, e.g. https://example.com/launch"
            aria-label="Event link"
            value={working.url}
            disabled={busy}
            onChange={(e) => patch({ url: e.target.value })}
          />

          <input
            className="field-input px-2 py-1 font-mono text-xs"
            placeholder="Timezone, e.g. Africa/Lagos"
            aria-label="Event timezone"
            value={working.timezone}
            disabled={busy}
            onChange={(e) => patch({ timezone: e.target.value })}
          />

          <textarea
            className="field-input px-2 py-1 text-base md:text-xs"
            rows={3}
            placeholder="Description (optional)"
            aria-label="Event description"
            value={working.description}
            disabled={busy}
            onChange={(e) => patch({ description: e.target.value })}
          />

          <input
            className="field-input px-2 py-1 font-mono text-xs"
            placeholder="UID (optional)"
            aria-label="Event UID"
            value={working.uid}
            disabled={busy}
            onChange={(e) => patch({ uid: e.target.value })}
          />

          <div className="flex items-center gap-2">
            <button type="submit" className="btn-primary px-3 py-1 text-xs" disabled={busy}>
              {busy ? 'Saving…' : invite ? 'Save invite' : 'Add invite'}
            </button>
            {invite ? (
              <button
                type="button"
                className="btn-ghost px-3 py-1 text-xs"
                disabled={busy}
                onClick={remove}
              >
                <Trash2 size={12} />
                Remove
              </button>
            ) : (
              <button
                type="button"
                className="btn-ghost px-3 py-1 text-xs"
                disabled={busy}
                onClick={cancel}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      )}

      {status !== 'idle' && (
        <p
          className={`text-xs ${
            status === 'error'
              ? 'text-danger'
              : status === 'saved' || status === 'removed'
                ? 'text-ok'
                : 'text-muted'
          }`}
        >
          {status === 'saving'
            ? 'Saving…'
            : (error ??
              (status === 'saved'
                ? 'Calendar invite saved.'
                : status === 'removed'
                  ? 'Calendar invite removed.'
                  : 'Not saved.'))}
        </p>
      )}
    </div>
  );
}
